import { useEffect, useMemo, useState } from 'react';
import { Loader2, Sparkles, ChevronDown, X, Search, ListChecks, Mic, Shuffle } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import * as apiClient from '@/services/apiClient';

const LOADING_MSGS = [
    'Analysing job description…',
    'Applying interview type & difficulty settings…',
    'Generating role-specific questions…',
    'Validating question set…',
    'Almost there…',
];

// ── SearchableSelect (local, same pattern as ScheduleInterviewPage) ────────────
function SearchableSelect({ id, label, value, onChange, options, placeholder, disabled, required }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const filtered = useMemo(() => {
        const q = query.toLowerCase();
        return q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
    }, [options, query]);

    const selected = options.find(o => o.value === value);

    useEffect(() => {
        if (!open) return;
        const close = (e) => {
            if (!e.target.closest(`[data-ss="${id}"]`)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open, id]);

    return (
        <div className="space-y-1.5" data-ss={id}>
            {label && <Label htmlFor={id}>{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>}
            <div className="relative">
                <button
                    id={id} type="button" disabled={disabled}
                    onClick={() => !disabled && setOpen(v => !v)}
                    className="flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <span className={!selected ? 'text-muted-foreground' : ''}>
                        {selected?.label || placeholder}
                    </span>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>

                {open && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
                        <div className="flex items-center gap-2 border-b px-3 py-2">
                            <Search className="h-3.5 w-3.5 text-muted-foreground" />
                            <input autoFocus className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} />
                            {query && <button type="button" onClick={() => setQuery('')}><X className="h-3 w-3 text-muted-foreground" /></button>}
                        </div>
                        <div className="max-h-48 overflow-y-auto py-1">
                            {filtered.length === 0
                                ? <p className="px-3 py-4 text-center text-xs text-muted-foreground">No options found.</p>
                                : filtered.map(o => (
                                    <button key={o.value} type="button"
                                        onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                                        className={`flex w-full flex-col items-start px-3 py-2 text-sm hover:bg-muted/60 ${value === o.value ? 'bg-primary/10 font-medium' : ''}`}
                                    >
                                        {o.label}
                                        {o.sub && <span className="text-xs text-muted-foreground">{o.sub}</span>}
                                    </button>
                                ))
                            }
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * NewQuestionSetModal
 *
 * Props:
 *   open          boolean
 *   onClose       () => void
 *   templates     InterviewTemplate[]
 *   masterTypes   InterviewMaster[]   (category = 'interviewType')
 *   masterDiffs   InterviewMaster[]   (category = 'difficulty')
 *   onCreated     (newSet) => void    — called after AI generation + save
 */
export function NewQuestionSetModal({ open, onClose, templates, masterTypes, masterDiffs, onCreated }) {
    const [templateId,   setTemplateId]   = useState('');
    const [typeId,       setTypeId]       = useState('');
    const [diffId,       setDiffId]       = useState('');
    const [qCount,       setQCount]       = useState(8);
    const [duration,     setDuration]     = useState(30);
    const [name,         setName]         = useState('');
    const [qFormat,      setQFormat]      = useState('verbal');
    const [loading,      setLoading]      = useState(false);
    const [loadingMsg,   setLoadingMsg]   = useState('');
    const [error,        setError]        = useState('');
    let msgInterval = null;

    // Auto-generate set name when template + type + difficulty all selected
    useEffect(() => {
        const tpl  = templates.find(t => t._id === templateId);
        const type = masterTypes.find(m => m._id === typeId);
        const diff = masterDiffs.find(m => m._id === diffId);
        if (tpl && type && diff) {
            setName(`${type.name} — ${diff.name} (${tpl.title})`);
        }
    }, [templateId, typeId, diffId, templates, masterTypes, masterDiffs]);

    const reset = () => {
        setTemplateId(''); setTypeId(''); setDiffId('');
        setQCount(8); setDuration(30); setName(''); setQFormat('verbal');
        setError('');
    };

    const handleClose = () => { if (!loading) { reset(); onClose(); } };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!templateId) { setError('Select a job description.'); return; }
        if (!typeId)     { setError('Select an interview type.'); return; }
        if (!diffId)     { setError('Select a difficulty level.'); return; }

        setLoading(true);
        setError('');
        let mi = 0;
        setLoadingMsg(LOADING_MSGS[mi]);
        msgInterval = setInterval(() => {
            mi = (mi + 1) % LOADING_MSGS.length;
            setLoadingMsg(LOADING_MSGS[mi]);
        }, 2800);

        try {
            const res = await apiClient.createQuestionSet({
                templateId,
                interviewTypeMasterId: typeId,
                difficultyMasterId:    diffId,
                numberOfQuestions:     Number(qCount),
                durationMinutes:       Number(duration),
                name:                  name.trim() || undefined,
                questionFormat:        qFormat,
            });
            clearInterval(msgInterval);
            reset();
            onClose();
            onCreated?.(res.data);
        } catch (err) {
            clearInterval(msgInterval);
            setError(err.message || 'Failed to generate question set.');
        } finally {
            clearInterval(msgInterval);
            setLoading(false);
        }
    };

    const templateOpts = useMemo(() =>
        templates.map(t => ({
            value: t._id,
            label: t.title,
            sub:   t.department || t.position || '',
        })), [templates]);

    const typeOpts = useMemo(() =>
        masterTypes.map(m => ({ value: m._id, label: m.name })), [masterTypes]);

    const diffOpts = useMemo(() =>
        masterDiffs.map(m => ({ value: m._id, label: m.name })), [masterDiffs]);

    const selectedType = masterTypes.find(m => m._id === typeId);
    const hasHints = selectedType?.requiredSkills?.length > 0 || selectedType?.topicAreas?.length > 0;

    return (
        <Dialog open={open} onOpenChange={v => !v && handleClose()}>
            <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-violet-500" />
                        New Question Set
                    </DialogTitle>
                    <DialogDescription>
                        AI generates a reusable set of questions from the job description.
                        You can edit each question after generation.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 pt-1">
                    {error && (
                        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                            {error}
                        </div>
                    )}

                    {/* Job Description */}
                    <SearchableSelect
                        id="qs-template" label="Job Description" required
                        value={templateId} onChange={setTemplateId}
                        options={templateOpts} placeholder="Select a job description…"
                        disabled={loading}
                    />

                    {/* Interview Type */}
                    <SearchableSelect
                        id="qs-type" label="Interview Type" required
                        value={typeId} onChange={setTypeId}
                        options={typeOpts} placeholder="e.g. Technical, Coding…"
                        disabled={loading}
                    />

                    {/* Show configured hints badge */}
                    {hasHints && (
                        <div className="flex flex-wrap gap-1.5 -mt-2 pl-0.5">
                            {selectedType.requiredSkills?.slice(0, 4).map(s => (
                                <span key={s} className="inline-flex items-center rounded-md border border-violet-500/25 bg-violet-500/8 px-1.5 py-0.5 text-[10px] text-violet-700 dark:text-violet-400">
                                    {s}
                                </span>
                            ))}
                            {selectedType.requiredSkills?.length > 4 && (
                                <span className="text-[10px] text-muted-foreground">+{selectedType.requiredSkills.length - 4} more</span>
                            )}
                        </div>
                    )}

                    {/* Difficulty */}
                    <SearchableSelect
                        id="qs-diff" label="Difficulty" required
                        value={diffId} onChange={setDiffId}
                        options={diffOpts} placeholder="e.g. Easy, Medium, Hard…"
                        disabled={loading}
                    />

                    {/* Count + Duration */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="qs-count">Questions <span className="text-destructive">*</span></Label>
                            <Input id="qs-count" type="number" min={3} max={30} value={qCount}
                                onChange={e => setQCount(e.target.value)} disabled={loading} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="qs-dur">Duration (min) <span className="text-destructive">*</span></Label>
                            <Input id="qs-dur" type="number" min={5} max={180} value={duration}
                                onChange={e => setDuration(e.target.value)} disabled={loading} />
                        </div>
                    </div>

                    {/* Question Format — Phase: MCQ/Verbal/Mixed */}
                    <div className="space-y-1.5">
                        <Label>Question Format</Label>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { value: 'verbal', label: 'Verbal',  icon: Mic,        desc: 'Open-ended spoken answers' },
                                { value: 'mcq',    label: 'MCQ',     icon: ListChecks, desc: 'Multiple choice + reasoning' },
                                { value: 'mixed',  label: 'Mixed',   icon: Shuffle,    desc: 'Blend of both formats' },
                            ].map(({ value, label, icon: Icon, desc }) => (
                                <button
                                    key={value}
                                    type="button"
                                    disabled={loading}
                                    onClick={() => setQFormat(value)}
                                    className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2.5 text-center transition-colors ${
                                        qFormat === value
                                            ? 'border-primary bg-primary/8 text-primary'
                                            : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    <span className="text-xs font-semibold">{label}</span>
                                    <span className="text-[10px] leading-tight">{desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Set name */}
                    <div className="space-y-1.5">
                        <Label htmlFor="qs-name">Set name</Label>
                        <Input id="qs-name" value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Auto-filled from selections above"
                            disabled={loading} />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading || !templateId || !typeId || !diffId}
                            className="gap-1.5">
                            {loading
                                ? <><Loader2 className="h-4 w-4 animate-spin" />{loadingMsg}</>
                                : <><Sparkles className="h-4 w-4" />Generate with AI</>
                            }
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default NewQuestionSetModal;
